import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IActivity } from '../../../../../../Activity/interface/IActivity'
import { IActivityCategories } from '../../../../../../Activity/interface/IActivityCategories'
import Spinner from '../../../../../../App/Components/Spinner/Spinner'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../../App/Hooks/reduxHooks'
import { Button } from '../../../../../../Common/Button'
import { updateSurveyTrigger } from '../../../../Store/Action'
import { DescriptionCardState } from '../EditDescriptionCard'
import {
  SelectedActivityList,
  SelectedActivityListItem,
  SelectedActivityRemoveIcon,
  SelectedActivityListItemLabel,
} from '../style'

import { EditDescriptionActivityModal } from './EditDescriptionActivityModal'
import { EditDescriptionCategoryModal } from './EditDescriptionCategoryModal'
import { FrequencyDropDown } from './FrequencyDropDown'

type ModalState = 'activity' | 'category'

type Props = {
  descriptionCardState: DescriptionCardState
  setDescriptionCardState: (arg: DescriptionCardState) => void
  surveyId: string
}

export const TriggerAfter: React.FC<Props> = ({
  descriptionCardState,
  setDescriptionCardState,
  surveyId,
}) => {
  const [modalState, setModalState] = useState<ModalState | null>(null)
  const [activityArray, setActivityArray] = useState<IActivity[]>([])
  const [categoryArray, setCategoryArray] = useState<IActivityCategories[]>([])

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const activityList = useAppSelector(state => state.activities.activity.list)
  const activityCategories = useAppSelector(
    state => state.activities.activityCategories.list,
  )

  type RemoveActivity = {
    mode: 'activity'
    currentData: IActivity[]
  }
  type RemoveCategory = {
    mode: 'category'
    currentData: IActivityCategories[]
  }
  const removeItem = (
    removeIndex: number,
    { mode, currentData }: RemoveActivity | RemoveCategory,
  ) => {
    const { trigger } = descriptionCardState
    if (!trigger || trigger.type !== 'send_after_appointment') {
      return
    }

    if (mode === 'activity' && activityList) {
      const newActivitiesArray = currentData.filter(
        (_, index) => index !== removeIndex,
      )
      const newUuidsArray = newActivitiesArray.map(activity => activity.uuid)

      setDescriptionCardState({
        ...descriptionCardState,
        currentState: 'loading',
      })

      dispatch(
        updateSurveyTrigger(surveyId, {
          ...trigger,
          activityUuids: newUuidsArray,
        }),
      ).then(() => {
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'idle',
          trigger: {
            ...trigger,
            activityUuids: newUuidsArray,
          },
        })
      })
    } else if (mode === 'category' && activityCategories) {
      const newIdsArray = currentData.map(category => category.id)
      newIdsArray.splice(removeIndex, 1)

      setDescriptionCardState({
        ...descriptionCardState,
        currentState: 'loading',
      })

      dispatch(
        updateSurveyTrigger(surveyId, {
          ...trigger,
          categoryIds: newIdsArray,
        }),
      ).then(() => {
        setDescriptionCardState({
          ...descriptionCardState,
          currentState: 'idle',
          trigger: {
            ...trigger,
            categoryIds: newIdsArray,
          },
        })
      })
    }
  }

  useMemo(() => {
    if (!descriptionCardState.trigger) return
    if (descriptionCardState.trigger.type !== 'send_after_appointment') return
    if (!activityList || !activityCategories) return
    const activityUuids = descriptionCardState.trigger.activityUuids
    const activityData = activityUuids
      .map(uuid => activityList.find(activity => activity.uuid === uuid))
      .filter(item => item !== undefined)

    const categoryData = descriptionCardState.trigger.categoryIds
      .map(categoryId =>
        activityCategories.find(category => category.id === categoryId),
      )
      .filter(item => item !== undefined)

    setActivityArray(activityData)
    setCategoryArray(categoryData)
  }, [descriptionCardState.trigger])

  if (!descriptionCardState.trigger) return null
  if (descriptionCardState.trigger.type !== 'send_after_appointment')
    return null
  if (!activityList || !activityCategories) return <Spinner />

  return (
    <div style={{ marginLeft: '8px' }}>
      <FrequencyDropDown
        setDescriptionCardState={setDescriptionCardState}
        descriptionCardState={descriptionCardState}
        surveyId={surveyId}
      />
      {modalState && modalState === 'activity' && (
        <EditDescriptionActivityModal
          activityList={activityList}
          activityCategories={activityCategories}
          setModalState={setModalState}
          setDescriptionCardState={setDescriptionCardState}
          surveyId={surveyId}
          descriptionCardState={descriptionCardState}
        />
      )}
      {modalState && modalState === 'category' && (
        <EditDescriptionCategoryModal
          activityCategories={activityCategories}
          setModalState={setModalState}
          setDescriptionCardState={setDescriptionCardState}
          surveyId={surveyId}
          descriptionCardState={descriptionCardState}
        />
      )}
      <p style={{ fontStyle: 'italic' }}>
        {t('Limit to only trigger on booking service.')}
      </p>

      <SelectedActivityList>
        {activityArray.length === 0 && categoryArray.length === 0 && (
          <p>{t('All services.')}</p>
        )}
        {activityArray.map((selected, index) => (
          <SelectedActivityListItem key={selected.name}>
            <SelectedActivityRemoveIcon
              icon={faTrash}
              onClick={() =>
                removeItem(index, {
                  mode: 'activity',
                  currentData: activityArray,
                })
              }
            />
            <SelectedActivityListItemLabel>
              {t('Activity')}
              &raquo; {selected.name}
            </SelectedActivityListItemLabel>
          </SelectedActivityListItem>
        ))}
        {categoryArray.map((selected, index) => (
          <SelectedActivityListItem key={selected.name}>
            <SelectedActivityRemoveIcon
              icon={faTrash}
              onClick={() =>
                removeItem(index, {
                  mode: 'category',
                  currentData: categoryArray,
                })
              }
            />
            <SelectedActivityListItemLabel>
              {t('Category')}
              &raquo; {selected.name}
            </SelectedActivityListItemLabel>
          </SelectedActivityListItem>
        ))}
      </SelectedActivityList>

      <div style={{ display: 'flex' }}>
        <div style={{ margin: ' 16px 0' }}>
          <Button onClick={() => setModalState('activity')}>
            <FontAwesomeIcon icon={faPlus} /> {t('Activity')}
          </Button>
        </div>
        <div style={{ margin: '16px 0 16px 8px' }}>
          <Button onClick={() => setModalState('category')}>
            <FontAwesomeIcon icon={faPlus} /> {t('Activity category')}
          </Button>
        </div>
      </div>
    </div>
  )
}
